import {
  AnimalCoverage,
  AnimalCoverageGroup,
  InsuranceCoverage,
} from "Store/interfaces/products/coverage";
import { formatDateString, reverseAndPadDDMMYYYY } from "Utils/dateUtils";
import {
  ACTUAL_PAYLOAD_PREFIX,
  CoverageAmountVetOption,
  DEFAULT_COVERAGE_AMOUNT_DEATH,
  DEFAULT_COVERAGE_AMOUNT_VET,
  NO_VET_OR_INJURIES_PREFIX,
} from "./animalConstants";
import {
  AnimalProductIntroFormValues,
  AnimalProductPriceFormValues,
} from "./animalInterfaces";

import { SearchItem } from "@storeblocks/autosuggest/lib/ListItem";
import { Option } from "@storeblocks/select";
import { DamageRecentYears } from "Components/Form/RecentDamages/recentDamagesInterfaces";
import { TextManager } from "Components/TextContext";
import { CoverageToAdd } from "Store/addProductToCart/hooks";
import { AddProduct } from "Store/addProductToCart/interfaces";
import { Breed } from "Store/findAnimalBreed/findAnimalBreedInterfaces";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { AnimalPriceRequestInfo } from "Store/interfaces/products/checkPrice/riskProducts/Animal";
import { Species } from "Store/interfaces/products/checkout/riskProducts/AnimalProduct";
import { CalculationPrice } from "Store/priceCalculation/priceCalculationInterfaces";
import { isDefined } from "Utils/functions";
import { FormikProps } from "formik";
import { CoveragePriceCalculation } from "../../../Tiles/ProductPriceTile/Coverages/coverages.types";

export const getAnimalSpeciesText = (
  isCapital: boolean,
  value?: Species,
  isGenitive = false,
  isIndefinite = false,
): string => {
  switch (value) {
    case Species.DOG:
      return isCapital
        ? handleIndefinite("Hunden", isIndefinite) + handleGenitive(isGenitive)
        : handleIndefinite("hunden", isIndefinite) + handleGenitive(isGenitive);
    case Species.CAT:
      return isCapital
        ? handleIndefinite("Katten", isIndefinite) + handleGenitive(isGenitive)
        : handleIndefinite("katten", isIndefinite) + handleGenitive(isGenitive);
    default: {
      return isCapital
        ? handleIndefinite("Dyret", isIndefinite) + handleGenitive(isGenitive)
        : handleIndefinite("dyret", isIndefinite) + handleGenitive(isGenitive);
    }
  }
};

export const handleGenitive = (isGenitive: boolean): string => {
  return isGenitive ? "s" : "";
};

export const handleIndefinite = (
  text: string,
  isIndefinite: boolean,
): string => {
  return isIndefinite ? text.replace(/.(?=.?$)/g, "") : text;
};

export const mapBreedDataToSearchItems = (breeds: Breed[]): SearchItem[] => {
  return breeds.map((breed: Breed) => {
    return {
      value: breed.breed,
    };
  });
};

export const mapAnimalCoverageToCoverageGroup = (
  coverages: InsuranceCoverage[],
): AnimalCoverageGroup => {
  if (coverages.length === 1) {
    switch (coverages[0]) {
      case AnimalCoverage.CAT_DEATH:
      case AnimalCoverage.DOG_DEATH_AND_INSURANCE_VALUE:
        return AnimalCoverageGroup.ANIMAL_DEATH;
      case AnimalCoverage.CAT_VET:
      case AnimalCoverage.DOG_VET:
        return AnimalCoverageGroup.ANIMAL_VETERINARY;
    }
  }

  return AnimalCoverageGroup.ANIMAL_DEATH_AND_VETERINARY;
};

export const mapAnimalCoverageGroupToAnimalCoverage = (
  species: Species,
  coverageGroup: AnimalCoverageGroup,
): AnimalCoverage[] => {
  switch (coverageGroup) {
    case AnimalCoverageGroup.ANIMAL_DEATH:
      return species === Species.DOG
        ? [AnimalCoverage.DOG_DEATH_AND_INSURANCE_VALUE]
        : [AnimalCoverage.CAT_DEATH];
    case AnimalCoverageGroup.ANIMAL_VETERINARY:
      return species === Species.DOG
        ? [AnimalCoverage.DOG_VET]
        : [AnimalCoverage.CAT_VET];
    case AnimalCoverageGroup.ANIMAL_DEATH_AND_VETERINARY:
      return species === Species.DOG
        ? [AnimalCoverage.DOG_DEATH_AND_INSURANCE_VALUE, AnimalCoverage.DOG_VET]
        : [AnimalCoverage.CAT_DEATH, AnimalCoverage.CAT_VET];
    default:
      return [];
  }
};

export const getCoverageSum = (
  priceValue: number,
  defaultValue: number,
): number => {
  if (priceValue === 0) {
    return defaultValue;
  }
  return priceValue;
};

export const mapAnimalFormToPriceRequest = (
  introValues: AnimalProductIntroFormValues,
  priceValues: AnimalProductPriceFormValues,
  clearOldPriceCalculations: boolean,
): AnimalPriceRequestInfo => {
  return {
    species: introValues.species,
    microchipped: introValues.microchipped,
    dateOfBirth: reverseAndPadDDMMYYYY(
      formatDateString(new Date(introValues.dateOfBirth)),
    ),
    breed: introValues.breed,

    coverageAmountDeath: isDefined(priceValues)
      ? getCoverageSum(
          priceValues.coverageAmountDeath,
          DEFAULT_COVERAGE_AMOUNT_DEATH,
        )
      : DEFAULT_COVERAGE_AMOUNT_DEATH,
    coverageAmountVet: isDefined(priceValues)
      ? getCoverageSum(
          priceValues.coverageAmountVet,
          DEFAULT_COVERAGE_AMOUNT_VET,
        )
      : DEFAULT_COVERAGE_AMOUNT_VET,
    injuries: Number(introValues.injuries),
    injuryYear: isDefined(introValues.injuryYear)
      ? Number(introValues.injuryYear)
      : undefined,
    showComposites: true,
    clearOldPriceCalculations: clearOldPriceCalculations,
  };
};

export const removeVetCoverageFromCoverages = (
  coverages: InsuranceCoverage[],
): InsuranceCoverage[] => coverages.filter(isDeathCoverage);

export const isVetCoverageSelected = (
  coverages: InsuranceCoverage[],
): boolean => {
  return coverages.includes(AnimalCoverage.DOG_VET) ||
    coverages.includes(AnimalCoverage.CAT_VET)
    ? true
    : false;
};

export const isDeathCoverageSelected = (
  coverages: InsuranceCoverage[],
): boolean => {
  return coverages.includes(AnimalCoverage.DOG_DEATH_AND_INSURANCE_VALUE) ||
    coverages.includes(AnimalCoverage.CAT_DEATH)
    ? true
    : false;
};

export const isDeathCoverage = (coverage: InsuranceCoverage): boolean =>
  coverage === AnimalCoverage.CAT_DEATH ||
  coverage === AnimalCoverage.DOG_DEATH_AND_INSURANCE_VALUE;

export const isVetCoverage = (coverage: InsuranceCoverage): boolean =>
  coverage === AnimalCoverage.CAT_VET || coverage === AnimalCoverage.DOG_VET;

export const isTwoOrMoreInjuries = (
  numberOfInjuries: DamageRecentYears,
): boolean => numberOfInjuries === DamageRecentYears.TWO_OR_MORE;

export const patchAnimalCoverages = (
  coverages: CoveragePriceCalculation[],
  isWithVet: boolean,
  // eslint-disable-next-line sonarjs/cognitive-complexity
): CoveragePriceCalculation[] => {
  if (!isDefined(coverages)) {
    return coverages;
  }

  return coverages.reduce((acc: any, curr: CoveragePriceCalculation) => {
    const [coverageName, values] = curr;
    const coverageNameWithoutPrefix = coverageName.startsWith(
      ACTUAL_PAYLOAD_PREFIX,
    )
      ? coverageName.replace(ACTUAL_PAYLOAD_PREFIX, "")
      : coverageName.replace(NO_VET_OR_INJURIES_PREFIX, "");

    if (isDeathCoverage(coverageNameWithoutPrefix as InsuranceCoverage)) {
      if (isWithVet && coverageName.startsWith(ACTUAL_PAYLOAD_PREFIX)) {
        acc = [...acc, [coverageNameWithoutPrefix, values]];
      }

      if (!isWithVet && coverageName.startsWith(NO_VET_OR_INJURIES_PREFIX)) {
        acc = [...acc, [coverageNameWithoutPrefix, values]];
      }
    } else {
      if (coverageName.startsWith(ACTUAL_PAYLOAD_PREFIX)) {
        acc = [...acc, [coverageNameWithoutPrefix, values]];
      }
    }

    return acc;
  }, []);
};

export const getAnimalProductToAdd = (
  coveragesToAdd: CoverageToAdd[],
  coveragesFromPriceCall: CoveragePriceCalculation[],
  species: Species,
): AddProduct => {
  const includedCoverages = coveragesFromPriceCall.filter((coverage) => {
    return coveragesToAdd.find((el) => el.coverage === coverage[0]);
  });

  return includedCoverages.reduce(
    (result, currentCoverage) => {
      const [coverageName, values] = currentCoverage;

      if (result.coverage !== "") {
        result.coverage =
          species === Species.CAT
            ? AnimalCoverage.CAT_DEATH_AND_VETERINARY
            : AnimalCoverage.DOG_DEATH_AND_VETERINARY;
      } else {
        result.coverage = coverageName;
      }

      result.priceData = calculateTotalPrice(
        result.priceData,
        values.prices[0],
      );

      return result;
    },
    {
      coverage: "",
      type: INSURANCE_NAME_VALUE.ANIMAL,
      priceData: {} as CalculationPrice,
    },
  );
};

export const calculateTotalPrice = (
  existingPrices: CalculationPrice | Record<string, never>,
  pricesToAdd: CalculationPrice,
): CalculationPrice => {
  const existingPricesArray = Object.entries(existingPrices);

  if (existingPricesArray.length === 0) {
    return pricesToAdd;
  }

  return existingPricesArray.reduce((result, currentPriceEntity) => {
    const correspondingPrice = Object.entries(pricesToAdd).filter(
      (price) => price[0] === currentPriceEntity[0],
    )[0];

    result = {
      ...result,
      [currentPriceEntity[0]]: sumPriceOrSetValue(
        currentPriceEntity,
        correspondingPrice,
      ),
    };

    return result;
  }, {} as CalculationPrice);
};

export const sumPriceOrSetValue = (
  currentPriceEntity: Record<string, any>,
  correspondingPrice: Record<string, any>,
): number | string => {
  if (currentPriceEntity[0] === "value") {
    return "Ingen";
  }

  if (currentPriceEntity[0] === "code") {
    return "N";
  }

  return currentPriceEntity[1] + correspondingPrice[1];
};

export const getNoItemsFoundText = (
  listOfBreeds: Breed[],
  formProps: FormikProps<AnimalProductIntroFormValues>,
  searchValue: string,
  isLoading: boolean,
): string => {
  const { getText } = TextManager;

  if (!isDefined(formProps.values.species)) {
    return getText("animalform.alertdescription.choosespecie");
  }

  if (
    isDefined(formProps.values.species) &&
    listOfBreeds.length === 0 &&
    isLoading
  ) {
    return getText("animalform.alertdescription.cantfetchbreeds");
  }

  if (isDefined(searchValue) && searchValue.length > 0) {
    const searchedBreedsResult = listOfBreeds.some(
      (instance) =>
        searchValue.toLocaleLowerCase() === instance.breed.toLocaleLowerCase(),
    );

    if (!searchedBreedsResult) {
      return getText("animalform.alertdescription.nosearchresult");
    }
  }

  return "";
};

export const isValidBreed = (listOfBreeds: Breed[], breed: string): boolean => {
  return listOfBreeds.some(
    (instance) =>
      instance.breed.toLocaleLowerCase() === breed.toLocaleLowerCase(),
  );
};

export const isAnimalCoverage = (coverage?: InsuranceCoverage): boolean =>
  isDefined(coverage) &&
  (<any>Object).values(AnimalCoverageGroup).includes(coverage);

export const getVetCoverageOptions = (): Option[] => {
  const { getText } = TextManager;
  return [
    {
      label: getText("animalform.optionlabel.0-coverageamountvet_0"),
      value: CoverageAmountVetOption.ZERO,
    },
    {
      label: getText("animalform.optionlabel.1-coverageamountvet_1"),
      value: CoverageAmountVetOption.ONE,
    },
    {
      label: getText("animalform.optionlabel.2-coverageamountvet_2"),
      value: CoverageAmountVetOption.TWO,
    },
    {
      label: getText("animalform.optionlabel.3-coverageamountvet_3"),
      value: CoverageAmountVetOption.THREE,
    },
    {
      label: getText("animalform.optionlabel.4-coverageamountvet_4"),
      value: CoverageAmountVetOption.FOUR,
    },
    {
      label: getText("animalform.optionlabel.5-coverageamountvet_5"),
      value: CoverageAmountVetOption.FIVE,
    },
  ];
};
